/**
 * useWebRTC - Hook para gestión de video P2P en partida online
 * 501 Dart League Online
 * 
 * Gestiona todo el ciclo de vida de la conexión WebRTC:
 * - Acceso a cámara/micrófono (getUserMedia)
 * - Señalización via Socket.IO (offer/answer/ICE)
 * - Conexión P2P con simple-peer
 * - Estados de setup, verificación y juego
 * - Controles de audio (mute/unmute)
 * - Detección de cámara perdida
 * 
 * USO:
 *   const webrtc = useWebRTC({ roomId, myPlayerNum, socketService });
 *   // webrtc.localVideoRef → <video ref>
 *   // webrtc.remoteVideoRef → <video ref>
 *   // webrtc.phase → 'idle' | 'requesting' | 'ready' | 'connecting' | 'connected' | 'verified' | 'error'
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import Peer from 'simple-peer';
import { getCameraPreference, saveCameraPreference } from './cameraPreference';
import dartLeagueAPI from '../api';

// Fallback: solo STUN (si no se obtienen credenciales TURN del backend)
const STUN_ONLY_SERVERS = [
  { urls: 'stun:stun.l.google.com:19302' },
  { urls: 'stun:stun1.l.google.com:19302' },
];

// Configuración de video para dardos (no necesitamos 4K, solo ver la diana)
const VIDEO_CONSTRAINTS = {
  width: { ideal: 640, max: 854 },   // 480p-480p+
  height: { ideal: 480, max: 480 },
  frameRate: { ideal: 24, max: 30 },
};

const AUDIO_CONSTRAINTS = {
  echoCancellation: true,
  noiseSuppression: true,
  autoGainControl: true,
};

const isMobileDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent || '');
};

/**
 * Fases del WebRTC:
 * - idle: sin iniciar
 * - requesting: pidiendo permisos de cámara
 * - ready: cámara local activa, esperando al peer
 * - connecting: intercambiando señales P2P
 * - connected: video P2P establecido
 * - verified: ambos jugadores confirmaron ver la diana
 * - error: algo falló
 * - camera-lost: cámara se perdió durante partida
 */

export default function useWebRTC({ roomId, myPlayerNum, socketService, enabled = true }) {
  const [phase, setPhase] = useState('idle');
  const [error, setError] = useState(null);
  const [myAudioMuted, setMyAudioMuted] = useState(false);
  const [rivalAudioMuted, setRivalAudioMuted] = useState(false);
  const [peerCameraReady, setPeerCameraReady] = useState(false);
  const [dianaStatus, setDianaStatus] = useState({ me: false, rival: false });
  const [cameraLost, setCameraLost] = useState(null); // { player, playerNum, timeout }
  const [setupComplete, setSetupComplete] = useState(false);
  const [remoteStreamActive, setRemoteStreamActive] = useState(false);
  const [remoteHasAudio, setRemoteHasAudio] = useState(true);
  const [audioAutoplayBlocked, setAudioAutoplayBlocked] = useState(false);
  const [localHasAudio, setLocalHasAudio] = useState(null);
  const [availableCameras, setAvailableCameras] = useState([]); // [{ deviceId, label }]
  const [selectedCameraId, setSelectedCameraId] = useState(() => getCameraPreference()?.deviceId || null);

  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const localStreamRef = useRef(null);
  const remoteStreamRef = useRef(null);
  const peerRef = useRef(null);
  const isInitiatorRef = useRef(false);
  const pendingSignalsRef = useRef([]);
  const peerStartedRef = useRef(false);
  const iceServersRef = useRef(STUN_ONLY_SERVERS); // Se actualiza con TURN al montar

  const hasLiveVideoTrack = useCallback((stream) => {
    const videoTrack = stream?.getVideoTracks?.()[0];
    return Boolean(videoTrack && videoTrack.readyState === 'live' && videoTrack.enabled !== false);
  }, []);

  // ─── Obtener credenciales TURN del backend al montar ───
  // Se ejecuta una vez, antes de que se cree el peer.
  // Si falla, iceServersRef queda con STUN_ONLY_SERVERS (comportamiento anterior).
  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;

    dartLeagueAPI.getTurnCredentials()
      .then((data) => {
        if (cancelled) return;
        if (data?.iceServers?.length) {
          iceServersRef.current = data.iceServers;
          console.log(`[useWebRTC] ICE servers cargados: ${data.iceServers.length} servers, TURN ${data.turnAvailable ? 'disponible' : 'no disponible'}`);
        }
      })
      .catch(() => {
        // Silencioso: STUN fallback ya está en iceServersRef
      });

    return () => { cancelled = true; };
  }, [enabled]);

  // Callback refs: reasignan el stream al <video> cuando cambia el elemento DOM
  // (necesario porque CameraSetup se desmonta y VideoOverlay monta nuevos <video>)
  const localVideoCallbackRef = useCallback((node) => {
    localVideoRef.current = node;
    if (node && localStreamRef.current) {
      node.srcObject = localStreamRef.current;
      node.play().catch(() => {});
    }
  }, []);

  const remoteVideoCallbackRef = useCallback((node) => {
    remoteVideoRef.current = node;
    if (node && remoteStreamRef.current) {
      node.srcObject = remoteStreamRef.current;
      node.play().catch((err) => {
        if (err.name === 'NotAllowedError') {
          // Autoplay bloqueado por el navegador: mutear para que el vídeo funcione
          node.muted = true;
          node.play().catch(() => {});
          setAudioAutoplayBlocked(true);
        }
      });
    }
  }, []);

  // ─── Cleanup: parar stream local ───
  const stopLocalStream = useCallback(() => {
    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => track.stop());
      localStreamRef.current = null;
    }
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = null;
    }
  }, []);

  // ─── Cleanup: destruir peer ───
  const destroyPeer = useCallback(() => {
    if (peerRef.current) {
      peerRef.current.destroy();
      peerRef.current = null;
    }
    peerStartedRef.current = false;
    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }
    setRemoteStreamActive(false);
  }, []);

  // ─── Cleanup total ───
  const cleanup = useCallback(() => {
    destroyPeer();
    stopLocalStream();
    setPhase('idle');
    setError(null);
    setPeerCameraReady(false);
    setDianaStatus({ me: false, rival: false });
    setCameraLost(null);
    setSetupComplete(false);
    setRemoteStreamActive(false);
    setAvailableCameras([]);
    setSelectedCameraId(null);
    pendingSignalsRef.current = [];
  }, [destroyPeer, stopLocalStream]);

  // ─── Enumerar cámaras disponibles ───
  const enumerateCameras = useCallback(async () => {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const cameras = devices
        .filter(d => d.kind === 'videoinput')
        .map((d, i) => ({
          deviceId: d.deviceId,
          label: d.label || `Cámara ${i + 1}`,
        }));
      setAvailableCameras(cameras);
      return cameras;
    } catch {
      return [];
    }
  }, []);

  // ─── Pedir acceso a cámara (con deviceId opcional) ───
  // isSwitch=true cuando se cambia de cámara (no resetear phase ni re-notificar al server)
  const requestCamera = useCallback(async (deviceId, { isSwitch = false, allowMobileRearFallback = true } = {}) => {
    if (!enabled) return;

    const preferredDeviceId = !deviceId ? getCameraPreference()?.deviceId : null;
    const targetDeviceId = deviceId || preferredDeviceId;

    try {
      if (!isSwitch) setPhase('requesting');
      setError(null);

      // Si se pasa un deviceId específico, usarlo; si no, dejar al navegador elegir
      const shouldPreferRearCamera = !targetDeviceId && isMobileDevice() && allowMobileRearFallback;
      const videoConstraints = targetDeviceId
        ? { ...VIDEO_CONSTRAINTS, deviceId: { exact: targetDeviceId } }
        : shouldPreferRearCamera
          ? { ...VIDEO_CONSTRAINTS, facingMode: { ideal: 'environment' } }
          : VIDEO_CONSTRAINTS;

      let stream;
      try {
        // Intentar con vídeo + audio
        stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
          audio: AUDIO_CONSTRAINTS,
        });
      } catch (mediaErr) {
        // Si falla por audio (micrófono bloqueado/inexistente), reintentar solo con vídeo
        const audioRelated = ['NotFoundError', 'NotAllowedError', 'NotReadableError'].includes(mediaErr.name);
        if (audioRelated) {
          console.warn('[useWebRTC] Audio no disponible, reintentando solo con vídeo:', mediaErr.name);
          stream = await navigator.mediaDevices.getUserMedia({
            video: videoConstraints,
            audio: false,
          });
        } else {
          throw mediaErr; // Error de vídeo real → propagar al catch exterior
        }
      }

      // Si ya había un stream previo (cambio de cámara), pararlo primero
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }

      localStreamRef.current = stream;

      // Detectar si el stream tiene audio
      const hasAudio = stream.getAudioTracks().length > 0;
      setLocalHasAudio(hasAudio);

      // Guardar deviceId seleccionado
      const videoTrack = stream.getVideoTracks()[0];
      if (videoTrack) {
        const settings = videoTrack.getSettings();
        const resolvedDeviceId = settings.deviceId || targetDeviceId || null;
        const resolvedLabel = videoTrack.label || '';
        setSelectedCameraId(resolvedDeviceId);
        if (resolvedDeviceId) {
          saveCameraPreference({ deviceId: resolvedDeviceId, label: resolvedLabel });
        }
      }

      // Mostrar preview local + play() explícito para móviles/tablets
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        localVideoRef.current.play().catch(() => {});
      }

      // Solo cambiar phase/notificar al server cuando es la primera activación
      if (!isSwitch) {
        setPhase('ready');
        // Enumerar cámaras (necesitamos permisos concedidos para ver los labels)
        await enumerateCameras();
        // Notificar al servidor que tenemos cámara
        socketService.emit('webrtc:camera-ready', { roomId });
      }

    } catch (err) {
      // Si falla el intento de cámara trasera en móvil, reintentar una vez sin facingMode
      if (!targetDeviceId && allowMobileRearFallback && isMobileDevice()) {
        const fallbackEligible = err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError';
        if (fallbackEligible) {
          return requestCamera(undefined, { isSwitch, allowMobileRearFallback: false });
        }
      }

      // Si el deviceId guardado ya no existe (desconectó webcam, cambió navegador), reintentar sin deviceId
      if (targetDeviceId && (err?.name === 'OverconstrainedError' || err?.name === 'NotFoundError' || err?.name === 'NotReadableError')) {
        console.warn('[useWebRTC] deviceId guardado no funciona, reintentando sin restricción:', targetDeviceId);
        return requestCamera(undefined, { isSwitch, allowMobileRearFallback: true });
      }

      console.error('[useWebRTC] Error accediendo a cámara:', err);
      setPhase('error');

      if (err.name === 'NotAllowedError') {
        setError('Debes permitir el acceso a la cámara para jugar online. Revisa los permisos del navegador.');
      } else if (err.name === 'NotFoundError') {
        setError('No se encontró ninguna cámara. Conecta una cámara web para jugar online.');
      } else if (err.name === 'NotReadableError') {
        setError('La cámara está siendo usada por otra aplicación. Ciérrala e inténtalo de nuevo.');
      } else if (err.name === 'OverconstrainedError') {
        setError('La cámara seleccionada no está disponible. Prueba con otra.');
      } else {
        setError(`Error al acceder a la cámara: ${err.message}`);
      }
    }
  }, [enabled, roomId, socketService, enumerateCameras]);

  // ─── Cambiar de cámara (solo disponible en la pantalla de configuración inicial) ───
  const switchCamera = useCallback(async (deviceId) => {
    if (!deviceId) return;

    // Re-pedir la cámara con el nuevo deviceId (sin resetear phase ni notificar al server)
    if (localStreamRef.current) {
      await requestCamera(deviceId, { isSwitch: true });
    }
  }, [requestCamera]);

  // ─── Crear peer P2P ───
  const createPeer = useCallback((initiator) => {
    if (!localStreamRef.current) return;
    if (peerStartedRef.current || peerRef.current) return;

    isInitiatorRef.current = initiator;
    peerStartedRef.current = true;
    setPhase('connecting');

    const peer = new Peer({
      initiator,
      stream: localStreamRef.current,
      trickle: true,
      config: { iceServers: iceServersRef.current },
    });

    // Enviar señal al servidor para retransmitir al peer
    peer.on('signal', (signal) => {
      socketService.emit('webrtc:signal', { roomId, signal });
    });

    // Recibir stream remoto
    peer.on('stream', (remoteStream) => {
      remoteStreamRef.current = remoteStream;
      setPhase((prev) => (prev === 'verified' ? prev : 'connected'));
      setRemoteStreamActive(hasLiveVideoTrack(remoteStream));

      // Detectar si el rival tiene pistas de audio activas
      const audioTracks = remoteStream?.getAudioTracks?.() || [];
      setRemoteHasAudio(audioTracks.some(t => t.readyState === 'live' && t.enabled));

      const videoTrack = remoteStream?.getVideoTracks?.()[0];
      if (videoTrack) {
        const updateRemoteTrackState = () => {
          setRemoteStreamActive(videoTrack.readyState === 'live' && videoTrack.enabled !== false);
        };

        videoTrack.addEventListener('ended', updateRemoteTrackState);
        videoTrack.addEventListener('mute', updateRemoteTrackState);
        videoTrack.addEventListener('unmute', updateRemoteTrackState);
      }

      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = remoteStream;
      }
    });

    // Conexión P2P establecida
    peer.on('connect', () => {
      setPhase('connected');
      socketService.emit('webrtc:connected', { roomId });
    });

    // Error
    peer.on('error', (err) => {
      console.error('[useWebRTC] Peer error:', err);
      // No setear error si ya estamos destruyendo
      if (peerRef.current) {
        setError(`Error en la conexión de video: ${err.message}`);
      }
    });

    // Peer cerrado
    peer.on('close', () => {
      console.log('[useWebRTC] Peer cerrado');
    });

    peerRef.current = peer;

    // Procesar señales pendientes (si llegaron antes de crear el peer)
    if (pendingSignalsRef.current.length > 0) {
      pendingSignalsRef.current.forEach(sig => {
        try { peer.signal(sig); } catch (e) { /* ignore */ }
      });
      pendingSignalsRef.current = [];
    }
  }, [roomId, socketService, hasLiveVideoTrack]);

  // ─── Toggle mute audio ───
  const toggleMyAudio = useCallback(() => {
    if (!localStreamRef.current) return;

    const audioTracks = localStreamRef.current.getAudioTracks();
    const newMuted = !myAudioMuted;

    audioTracks.forEach(track => {
      track.enabled = !newMuted;
    });

    setMyAudioMuted(newMuted);
    socketService.emit('webrtc:toggle-audio', { roomId, muted: newMuted });
  }, [myAudioMuted, roomId, socketService]);

  // ─── Reintentar activar micrófono (para usar desde CameraSetup) ───
  const retryLocalAudio = useCallback(async () => {
    if (!localStreamRef.current) return;
    // Ya tiene audio → no hacer nada
    if (localStreamRef.current.getAudioTracks().length > 0) return;
    try {
      const audioStream = await navigator.mediaDevices.getUserMedia({ audio: AUDIO_CONSTRAINTS });
      const audioTrack = audioStream.getAudioTracks()[0];
      if (audioTrack) {
        localStreamRef.current.addTrack(audioTrack);
        setLocalHasAudio(true);
        setMyAudioMuted(false);
      }
    } catch {
      // El usuario denegó o no tiene micro → se queda como está
    }
  }, []);

  // ─── Mute audio del rival (local, no afecta al peer) ───
  const toggleRivalAudio = useCallback(() => {
    if (!remoteVideoRef.current) return;
    const newMuted = !rivalAudioMuted;
    remoteVideoRef.current.muted = newMuted;
    setRivalAudioMuted(newMuted);
    // Si desmutea y había autoplay bloqueado, aprovechar el gesto para desbloquear
    if (!newMuted && audioAutoplayBlocked) {
      remoteVideoRef.current.play()
        .then(() => setAudioAutoplayBlocked(false))
        .catch(() => {});
    }
  }, [rivalAudioMuted, audioAutoplayBlocked]);

  // ─── Desbloquear audio del rival (requiere gesto del usuario) ───
  const retryRemoteAudio = useCallback(() => {
    if (!remoteVideoRef.current) return;
    remoteVideoRef.current.muted = false;
    setRivalAudioMuted(false);
    remoteVideoRef.current.play()
      .then(() => setAudioAutoplayBlocked(false))
      .catch(() => {});
  }, []);

  // ─── Confirmar que veo la diana del rival ───
  const confirmDiana = useCallback(() => {
    if (!remoteStreamActive) {
      setError('Aún no recibes vídeo real del rival. Espera unos segundos o reinicia el vídeo.');
      return;
    }

    socketService.emit('webrtc:diana-confirmed', { roomId });
    setDianaStatus(prev => ({ ...prev, me: true }));
  }, [remoteStreamActive, roomId, socketService]);

  const requestPeerReset = useCallback(() => {
    destroyPeer();
    pendingSignalsRef.current = [];
    setSetupComplete(false);
    setDianaStatus({ me: false, rival: false });
    setRemoteStreamActive(false);

    if (localStreamRef.current) {
      setPhase('ready');
      socketService.emit('webrtc:camera-ready', { roomId });
    } else {
      setPhase('idle');
    }

    socketService.emit('webrtc:reset-request', { roomId });
  }, [destroyPeer, roomId, socketService]);

  // ─── Socket listeners ───
  useEffect(() => {
    if (!enabled || !socketService) return;

    // Peer también tiene cámara lista
    const handlePeerCameraReady = () => {
      setPeerCameraReady(true);
    };

    // Ambos listos → decidir quién es initiator
    const handleBothReady = ({ initiator }) => {
      const isInitiator = myPlayerNum === initiator;
      createPeer(isInitiator);
    };

    // Señal del peer (SDP/ICE)
    const handleSignal = ({ signal }) => {
      if (peerRef.current) {
        try {
          peerRef.current.signal(signal);
        } catch (e) {
          console.error('[useWebRTC] Error procesando señal:', e);
        }
      } else {
        // Guardar para procesar cuando el peer se cree
        pendingSignalsRef.current.push(signal);
      }
    };

    // Estado de diana
    const handleDianaStatus = (status) => {
      setDianaStatus({
        me: myPlayerNum === 1 ? status.player1Diana : status.player2Diana,
        rival: myPlayerNum === 1 ? status.player2Diana : status.player1Diana,
      });
    };

    // Setup completo (ambos confirman diana)
    const handleSetupComplete = () => {
      setSetupComplete(true);
      setPhase('verified');
    };

    const handleResetStarted = () => {
      destroyPeer();
      pendingSignalsRef.current = [];
      setSetupComplete(false);
      setDianaStatus({ me: false, rival: false });
      setRemoteStreamActive(false);

      if (localStreamRef.current) {
        setPhase('ready');
      } else {
        setPhase('idle');
      }
    };

    // Cámara del rival perdida
    const handleCameraLostNotify = (data) => {
      if (data.playerNum !== myPlayerNum) {
        setCameraLost(data);
      }
    };

    // Cámara del rival restaurada
    const handleCameraRestoredNotify = () => {
      setCameraLost(null);
    };

    // Timeout de cámara (partida nula)
    const handleCameraTimeout = (data) => {
      setCameraLost({ ...data, expired: true });
    };

    const cleanups = [
      socketService.on('webrtc:peer-camera-ready', handlePeerCameraReady),
      socketService.on('webrtc:both-ready', handleBothReady),
      socketService.on('webrtc:signal', handleSignal),
      socketService.on('webrtc:diana-status', handleDianaStatus),
      socketService.on('webrtc:setup-complete', handleSetupComplete),
      socketService.on('webrtc:reset-started', handleResetStarted),
      socketService.on('webrtc:camera-lost-notify', handleCameraLostNotify),
      socketService.on('webrtc:camera-restored-notify', handleCameraRestoredNotify),
      socketService.on('webrtc:camera-timeout', handleCameraTimeout),
    ];

    return () => {
      cleanups.forEach(c => typeof c === 'function' && c());
    };
  }, [enabled, socketService, myPlayerNum, createPeer, destroyPeer]);

  // ─── Detectar pérdida de cámara local ───
  useEffect(() => {
    if (!localStreamRef.current || !enabled) return;

    const videoTrack = localStreamRef.current.getVideoTracks()[0];
    if (!videoTrack) return;

    const handleTrackEnded = () => {
      console.warn('[useWebRTC] Video track ended (cámara desconectada)');
      socketService.emit('webrtc:camera-lost', { roomId });
      setPhase('camera-lost');
    };

    videoTrack.addEventListener('ended', handleTrackEnded);

    // También detectar via onmute (algunos navegadores)
    const handleTrackMute = () => {
      console.warn('[useWebRTC] Video track muted (posible pérdida de cámara)');
    };
    videoTrack.addEventListener('mute', handleTrackMute);

    return () => {
      videoTrack.removeEventListener('ended', handleTrackEnded);
      videoTrack.removeEventListener('mute', handleTrackMute);
    };
  }, [phase, enabled, roomId, socketService]);

  // ─── Cleanup al desmontar ───
  useEffect(() => {
    return () => {
      cleanup();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return {
    // Callback refs para los elementos <video> (se reasignan al cambiar de componente)
    localVideoRef: localVideoCallbackRef,
    remoteVideoRef: remoteVideoCallbackRef,

    // Ref directa al stream local (para comprobar estado de la cámara sin callback ref)
    localStreamRef,

    // Estado
    phase,
    error,
    peerCameraReady,
    dianaStatus,
    cameraLost,
    setupComplete,
    remoteStreamActive,

    // Audio
    myAudioMuted,
    rivalAudioMuted,
    remoteHasAudio,
    audioAutoplayBlocked,
    localHasAudio,

    // Cámaras disponibles
    availableCameras,
    selectedCameraId,

    // Acciones
    requestCamera,
    switchCamera,
    toggleMyAudio,
    toggleRivalAudio,
    retryRemoteAudio,
    retryLocalAudio,
    confirmDiana,
    requestPeerReset,
    cleanup,
  };
}
